import { lighten, transparentize } from 'polished';

const theme = {
  fontFamily: "'Roboto', sans-serif",
  fontWeight: {
    light: 300,
    regular: 400,
    bold: 500,
  },
  fontSize: {
    xxs: '12px',
    xs: '14px',
    sm: '16px',
    md: '18px',
    lg: '22px',
    xl: '24px',
  },
  spacing: {
    xxs: '4px',
    xs: '8px',
    sm: '12px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
  },
  shadow: {
    xs: '0px 1px 3px 0px rgba(0, 0, 0, 0.15)',
    sm: '0px 4px 8px 0px rgba(0, 0, 0, 0.15)',
    md: '0px 8px 16px 0px rgba(0, 0, 0, 0.15)',
    lg: '0 3.2px 7.2px 0 rgba(0, 0, 0, .132),0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
  },
  borderRadius: {
    xs: '2px',
    sm: '4px',
    md: '4px',
    lg: '4px',
  },
  transition: 'all 0.3s ease',
  ToggleSwitch: {
    trackGap: '3.2px',
    width: '40px',
    height: '20.38px',
    borderRadius: '100px',
    trackSize: '10px',
    borderWidth: '1px',
    transition: '100ms cubic-bezier(0, 0, 0.35, 1)',
  },
};

const colors = {
  primary: '#0078d4',
  primaryHover: '#005a9e',
  primaryHighlight: '#deecf9',
  hoverLight: '#E6E9EF',
  private: '#d13438',
  shareable: '#b4009e',
  black: '#000000',
  dark: '#1D1D1C',
  greyDark: '#676879',
  grey: '#C5C7D0',
  greyActive: '#edebe9',
  greyLight: '#E6E9EF',
  greyLighting: '#f3f2f1',
  light: '#F5F6F8',
  white: '#FFFFFF',
  eggYolk: '#FFCB00',
  green: '#00C875',
  greenMiddle: '#027D00',
  greenDark: '#007000',
  blueDark: '#1F3244',
};

const components = {
  globalStyle: {
    body: {
      fontSize: theme.fontSize.xs,
    },
  },
  Button: {
    default: {
      borderRadius: theme.borderRadius.xs,
      fontWeight: theme.fontWeight.bold,
    },
    sizes: {
      sm: {
        fontSize: theme.fontSize.xs,
        height: 32,
        paddingLeft: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
      },
      md: {
        fontSize: theme.fontSize.xs,
        height: 32,
        paddingLeft: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
      },
      lg: {
        fontSize: theme.fontSize.xs,
        height: 32,
        paddingLeft: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
      },
    },
  },
  Title: {
    default: {
      fontWeight: theme.fontWeight.bold,
      lineHeight: '100%',
    },
    sizes: {
      sm: {
        fontSize: theme.fontSize.sm,
      },
      md: {
        fontSize: theme.fontSize.md,
      },
      lg: {
        fontSize: theme.fontSize.lg,
      },
    },
  },
  Card: {
    default: {
      padding: theme.spacing.sm,
      borderRadius: theme.borderRadius.xs,
      boxShadow: theme.shadow.xs,
    },
    pressable: {
      border: 0,
      ':hover': {
        boxShadow: theme.shadow.lg,
      },
    },
  },
  AddCard: {
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.bold,
  },
  TextField: {
    borderRadius: theme.borderRadius.xs,
    height: 38,
    fontSize: theme.fontSize.xs,
    ':focus': {
      outline: 'none',
    },
  },
  AddButton: {
    fontSize: theme.fontSize.xs,
  },
  StepBar: {
    Container: {
      boxShadow: 'none',
    },
    StepLabel: {
      fontSize: theme.fontSize.xs,
    },
  },
  ColumnBoard: {
    boxShadow: 'none',
    borderRadius: theme.borderRadius.xs,
  },
  ActionItem: {
    container: {
      borderRadius: `${theme.borderRadius.xs} 0 0 ${theme.borderRadius.xs}`,
      borderLeftWidth: 2,
    },
    button: {
      borderRadius: `0 ${theme.borderRadius.xs} ${theme.borderRadius.xs} 0`,
    },
  },
  CardSummary: {
    container: {},
    number: {
      fontSize: 50,
    },
  },
};

const lightThemeAzure = {
  ...theme,
  palette: {
    ...colors,
    bgHoverGrey: colors.hoverLight,
    bgBody: colors.white,
    popUp: colors.white,
    colorBody: colors.dark,
    bgBodyTransparent: transparentize(0.2, colors.white),
    bgCard: colors.white,
    borderCard: colors.greyLight,
    borderCardHover: colors.primary,
    textContrast1: colors.dark,
    textContrast2: colors.greyDark,
    textContrast3: colors.grey,
    textLink: colors.primary,
    textLinkHover: colors.primaryHover,
    titleCard: colors.dark,
    titleCardHover: colors.primary,
    bgInvertBody: colors.greyLight,
    colorInvertBody: colors.black,
    bgButtonPrimary: colors.primary,
    bgButtonPrimaryHover: colors.primaryHover,
    bgButtonPrimaryActive: colors.primaryHover,
    bgButtonPrimaryDisabled: colors.greyLight,
    colorButtonPrimary: colors.white,
    colorButtonPrimaryDisabled: colors.grey,
    bgButtonSecondary: 'transparent',
    bgButtonSecondaryHover: colors.greyLight,
    bgButtonSecondaryActive: colors.primaryHighlight,
    bgButtonSecondaryDisabled: 'transparent',
    colorButtonSecondary: colors.primary,
    colorButtonSecondaryDisabled: colors.grey,
    bgInput: colors.white,
    colorInput: colors.dark,
    borderInput: colors.grey,
    borderInputFocus: colors.primary,
    borderInputButton: colors.primary,
    borderInputButtonHover: colors.primaryHover,
    bgColumnBoard: colors.greyLight,
    bgColumnBoardDragging: colors.primaryHighlight,
    colorStepbarLine: colors.greyLight,
    colorStepbarLineActive: colors.primary,
    bgCardBoard: colors.white,
    bgCardBoardDragging: colors.light,
    bgCardBoardCombining: colors.primary,
    colorAddButton: colors.greyDark,
    colorAddButtonHover: colors.primary,
    bgCardIdea: colors.white,
    bgCardIdeaDragging: colors.greyLight,
    bgCardIdeaCombining: colors.primary,
    colorCardIdeaIcon: colors.greyDark,
    colorCardIdea: colors.dark,
    colorCardIdeaCombining: colors.white,
    bgActivityInput: colors.white,
    bgActivityInputReadyOnly: colors.light,
    colorActivityInput: colors.dark,
    colorActivityInputDisabled: colors.grey,
    borderActivityInput: colors.greyLight,
    bgDropdown: colors.white,
    bgDropdownItemHover: colors.greyLight,
    colorDropdownItem: colors.dark,
    colorDropdownItemHover: colors.dark,
    badgeNStarted: colors.greyLight,
    badgeInProgress: colors.eggYolk,
    badgeDone: colors.green,
    danger: colors.private,

    switchColor: '',
    switchCheckedColor: colors.primary,
    switchCheckedDisabledColor: colors.primary,
    switchTrackColor: transparentize(0.3, colors.black),
    switchTrackCheckedColor: colors.white,
    switchBorderColor: transparentize(0.3, colors.black),
    bannerInfoBackgroundColor: colors.primaryHighlight,
    bannerInfoBorderColor: colors.primary,
  },
  selectStyle: {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? colors.primary : colors.grey,
      ':hover': {
        borderColor: state.isFocused ? colors.primary : colors.grey,
      },
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      pointerEvents: 'auto',
      height: 'fit-content',
      paddingLeft: 8,
      borderRadius: 2,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: 8,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: 8,
      marginBottom: 8,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: 8,
    }),
    multiValue: (provided) => ({
      ...provided,
      borderRadius: 2,
      margin: 2,
    }),
    menu: (provided) => ({
      ...provided,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    option: (provided) => ({
      ...provided,
      padding: '6px 10px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      padding: '0 2px',
    }),
  },
  components,
};

const darkThemeAzure = {
  ...theme,
  palette: {
    ...colors,
    bgHoverGrey: colors.greyDark,
    bgBody: colors.dark,
    colorBody: colors.light,
    bgBodyTransparent: transparentize(0.2, colors.dark),
    bgCard: lighten(0.1, colors.dark),
    bgInvertBody: colors.white,
    colorInvertBody: colors.grey,
    borderCard: colors.dark,
    borderCardHover: colors.greyDark,
    textContrast1: colors.white,
    textContrast2: colors.greyLight,
    textContrast3: colors.grey,
    textLink: lighten(0.2, colors.primary),
    textLinkHover: colors.primaryHighlight,
    titleCard: colors.white,
    titleCardHover: colors.primaryHighlight,
    bgButtonPrimary: colors.primary,
    bgButtonPrimaryHover: colors.primaryHover,
    bgButtonPrimaryActive: colors.primaryHover,
    bgButtonPrimaryDisabled: colors.greyDark,
    colorButtonPrimary: colors.white,
    colorButtonPrimaryDisabled: transparentize(0.5, colors.grey),
    bgButtonSecondary: 'transparent',
    bgButtonSecondaryHover: lighten(0.05, colors.dark),
    bgButtonSecondaryActive: colors.greyDark,
    bgButtonSecondaryDisabled: 'transparent',
    colorButtonSecondary: lighten(0.2, colors.primary),
    colorButtonSecondaryDisabled: colors.greyDark,
    bgInput: colors.dark,
    colorInput: colors.light,
    borderInput: colors.greyDark,
    borderInputFocus: colors.primary,
    borderInputButton: colors.primary,
    borderInputButtonHover: colors.greyDark,
    bgColumnBoard: lighten(0.1, colors.dark),
    bgColumnBoardDragging: lighten(0.2, colors.dark),
    colorStepbarLine: lighten(0.1, colors.dark),
    colorStepbarLineActive: colors.primary,
    colorAddButton: colors.light,
    colorAddButtonHover: lighten(0.2, colors.primary),
    bgCardIdea: colors.dark,
    bgCardIdeaDragging: lighten(0.1, colors.dark),
    bgCardIdeaCombining: colors.primaryHover,
    colorCardIdea: colors.greyLight,
    colorCardIdeaCombining: colors.white,
    bgActivityInput: colors.dark,
    bgActivityInputReadyOnly: lighten(0.05, colors.dark),
    colorActivityInput: colors.light,
    colorActivityInputDisabled: colors.grey,
    borderActivityInput: colors.greyDark,
    bgDropdown: colors.dark,
    bgDropdownItemHover: lighten(0.05, colors.dark),
    colorDropdownItem: colors.greyLight,
    colorDropdownItemHover: colors.grayLight,
    badgeNStarted: colors.greyLight,
    badgeInProgress: colors.eggYolk,
    badgeDone: colors.green,
    danger: colors.private,

    switchColor: '',
    switchCheckedColor: colors.primary,
    switchCheckedDisabledColor: colors.primary,
    switchTrackColor: transparentize(0.2, colors.white),
    switchTrackCheckedColor: lighten(0.1, colors.dark),
    switchBorderColor: colors.white,
    bannerInfoBackgroundColor: colors.blueDark,
    bannerInfoBorderColor: colors.primary,
  },
  selectStyle: {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: colors.dark,
      borderColor: state.isFocused ? colors.primary : colors.greyDark,
      ':hover': {
        borderColor: state.isFocused ? colors.primary : colors.greyDark,
      },
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      pointerEvents: 'auto',
      height: 'fit-content',
      paddingLeft: 8,
      borderRadius: 2,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: colors.light,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: colors.greyDark,
      padding: 8,
    }),
    input: (provided) => ({
      ...provided,
      color: colors.light,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: colors.greyDark,
      marginTop: 8,
      marginBottom: 8,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: colors.greyDark,
      padding: 8,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: colors.greyDark,
      borderRadius: 2,
      margin: 2,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: colors.light,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: colors.dark,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    option: (provided, state) => ({
      ...provided,
      // eslint-disable-next-line no-nested-ternary
      backgroundColor: state.isSelected
        ? colors.primary
        : state.isFocused
        ? transparentize(0.5, colors.greyDark)
        : 'transparent',
      padding: '6px 10px',
      ':active': {
        backgroundColor: transparentize(0.8, colors.greyDark),
      },
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      padding: '0 2px',
    }),
  },
  components,
};

export { lightThemeAzure, darkThemeAzure };
